import React from 'react';
import { Clock } from 'lucide-react';
import { LatestContent } from '../../../services';
import { getImageUrl } from '../../../utils';

const WEB_BASE_URL = process.env.REACT_APP_WEB_URL || 'http://localhost:3000';

const getNewsUrl = (slug: string | undefined, contentId: number): string => {
  const baseUrl = WEB_BASE_URL.endsWith('/') ? WEB_BASE_URL.slice(0, -1) : WEB_BASE_URL;
  // Slug varsa slug kullan, yoksa id kullan
  return slug ? `${baseUrl}/i/${slug}?ig=true` : `${baseUrl}/i/${contentId}?ig=true`;
};

export interface LatestContentCardProps {
  content: LatestContent;
}

export const LatestContentCard = ({ content }: LatestContentCardProps) => {
  let formattedDate = 'Tarih bilgisi yok';
  if (content.createdOn) {
    try {
      const createdDate = new Date(content.createdOn);
      if (!isNaN(createdDate.getTime())) {
        formattedDate = createdDate.toLocaleDateString('tr-TR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    } catch (error) {
      console.error('Tarih parse hatası:', error, content.createdOn);
    }
  }

  const truncatedContent = content.content
    ? content.content.length > 150
      ? content.content.substring(0, 150) + '...'
      : content.content
    : 'İçerik bulunmuyor';

  const allMedia = [...(content.images || []), ...(content.videos || [])];
  const hasMedia = allMedia.length > 0 || !!content.thumbnail;

  const firstImage = content.images?.[0];
  const firstVideo = content.videos?.[0];
  const displayImage = firstImage
    ? getImageUrl(firstImage.low_quality || firstImage.original)
    : firstVideo?.low_quality
      ? getImageUrl(firstVideo.low_quality)
      : content.thumbnail
        ? getImageUrl(content.thumbnail)
        : getImageUrl(undefined);

  const newsUrl = getNewsUrl(content.slug, content.id);

  const handleCardClick = () => {
    window.open(newsUrl, '_blank');
  };

  return (
    <div
      className="bg-white rounded-2xl pt-[10px] pl-[24px] pr-[24px] pb-[10px] mb-[10px] flex flex-col cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleCardClick}
    >
      {/* Title */}
      {content.title ? (
        <h3 className="text-md font-semibold text-gray-900 mb-[4px] line-clamp-2">
          {content.title}
        </h3>
      ) : null}

      {/* Content preview */}
      {content.content ? (
        <div className="text-gray-600 text-sm leading-[17.4px] font-normal mb-[10px] line-clamp-3 h-[52.2px] overflow-hidden">
          {truncatedContent}
        </div>
      ) : null}

      {/* Media Gallery */}
      {hasMedia && (
        <div className="mb-[10px]">
          {allMedia.length > 3 ? (
            // 4+ medya: 2x2 grid (simetrik)
            <div className="flex gap-[2px] w-full rounded-md overflow-hidden">
              <div className="flex-1 flex flex-col gap-[2px]">
                {allMedia.slice(0, 2).map((media, index) => (
                  <div key={media.id || index} className="w-full aspect-video rounded-md overflow-hidden">
                    <img
                      src={getImageUrl(media.low_quality || media.original)}
                      alt={`Media ${index + 1}`}
                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
              <div className="flex-1 flex flex-col gap-[2px] relative">
                {allMedia.slice(2, 4).map((media, index) => {
                  const remaining = allMedia.length - 4;
                  const isLast = index === 1;
                  return (
                    <div key={media.id || index} className="w-full aspect-video rounded-md overflow-hidden relative">
                      <img
                        src={getImageUrl(media.low_quality || media.original)}
                        alt={`Media ${index + 3}`}
                        className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                      />
                      {isLast && remaining > 0 && (
                        <div className="absolute bottom-2 right-2 bg-blue-600 bg-opacity-70 text-white text-xs px-2 py-1 rounded-full font-medium">
                          +{remaining}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : allMedia.length === 3 ? (
            // 3 medya: 1 büyük + 2 küçük (simetrik - sol taraf sağ taraftaki 2 medya + gap yüksekliğine eşit)
            <div className="flex gap-[2px] w-full rounded-md overflow-hidden items-stretch">
              <div className="flex-1 flex flex-col rounded-md overflow-hidden">
                <div className="flex-1 rounded-md overflow-hidden">
                  <img
                    src={getImageUrl(allMedia[0]?.low_quality || allMedia[0]?.original)}
                    alt="Main media"
                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>
              <div className="flex-1 flex flex-col gap-[2px]">
                {allMedia.slice(1, 3).map((media, index) => (
                  <div key={media.id || index} className="w-full aspect-video rounded-md overflow-hidden">
                    <img
                      src={getImageUrl(media.low_quality || media.original)}
                      alt={`Side media ${index + 1}`}
                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : allMedia.length === 2 ? (
            // 2 medya: yan yana (simetrik - tek medya ile aynı yükseklikte)
            <div className="flex gap-[2px] w-full aspect-video rounded-md overflow-hidden">
              {allMedia.map((media, index) => (
                <div
                  key={media.id || index}
                  className="flex-1 h-full rounded-md overflow-hidden"
                >
                  <img
                    src={getImageUrl(media.low_quality || media.original)}
                    alt={`Media ${index + 1}`}
                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          ) : displayImage ? (
            // 1 medya veya thumbnail (simetrik)
            <div className="w-full aspect-video rounded-md overflow-hidden">
              <img
                src={displayImage}
                alt="Content thumbnail"
                className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
              />
            </div>
          ) : null}
        </div>
      )}

      {/* Date */}
      <div className="flex items-center gap-1 text-zinc-400 text-[10px] font-normal">
        <Clock className="w-3 h-3" />
        <span>{formattedDate}</span>
      </div>
    </div>
  );
};

